import { css } from "@knyt/tailor";
import { __isKnytElementComposed, __knytElementComposedHotUpdate, __knytElementComposedLifecycle, __knytElementComposedRenderer, } from "../constants.js";
import { getConstructorStaticMember } from "../getConstructorStaticMember.js";
import { isContainerModeEnabled, KnytElement, } from "../KnytElement.js";
import { performHotUpdate } from "../performHotUpdate.js";
import { defineElementDefinition, } from "./defineElementDefinition.js";
/**
 * @internal scope: package
 */
// TODO: Update parameters to start with `tagName` like:
// `defineKnytElement(tagName, options);`
export function defineKnytElement(params) {
    const ElementConstructor = class extends KnytElement {
        static [__isKnytElementComposed] = true;
        static [__knytElementComposedLifecycle] = params.lifecycle;
        // This can't be an arrow function, because we need to access `this` in the method.
        // See comments below.
        static [__knytElementComposedHotUpdate](params) {
            // TODO: Remove in production builds, and replace with a no-op containing
            // a comment that explains that HMR is not supported in production builds.
            performHotUpdate({
                ...params,
                // Note: We need to use `this` here to refer to the current constructor.
                // Referencing `ElementConstructor` doesn't work, because it's not
                // the constructor that's being updated during HMR. During HMR,
                // a new constructor is created, and `this` refers to that new constructor.
                prevConstructor: this,
            }).catch((error) => {
                console.error(`[Luthier:HMR] Error during hot update for <${params.tagName}>:`, error);
            });
        }
        /**
         * Casted as non-nullable, because it will be set in the constructor
         * by the `setComposedRenderer` function.
         */
        [__knytElementComposedRenderer];
        constructor() {
            super(params.options);
            const host = this;
            const lifecycle = getConstructorStaticMember(this, __knytElementComposedLifecycle);
            setComposedRenderer(this, lifecycle);
        }
        render() {
            const host = this;
            const renderer = this[__knytElementComposedRenderer];
            return renderer.call(host, host);
        }
    };
    ElementConstructor.properties =
        params.properties ?? {};
    ElementConstructor.styleSheet = getStyleSheet(params);
    const { renderMode, customElements } = params.options ?? {};
    const ElementBuilders = defineElementDefinition(params.tagName, ElementConstructor, {
        defaultRenderMode: renderMode,
        customElements,
    });
    return ElementBuilders;
}
/**
 * Sets the renderer function for a `KnytElementComposed` instance based
 * on the provided lifecycle function.
 *
 * @internal scope: workspace
 */
export function setComposedRenderer(element, lifecycle) {
    const host = element;
    element[__knytElementComposedRenderer] =
        lifecycle.call(host, host) ?? (() => null);
}
/**
 *  Retrieves the style sheet for a Knyt element based on the provided parameters.
 */
function getStyleSheet(params) {
    const { options, styleSheet, tagName } = params;
    // If a styleSheet is provided, return it directly.
    // Do not check for the existence of `styleSheet` in the options object,
    // as users may intentionally set `styleSheet` to `undefined` to disable
    // the default style sheet. This approach allows users to either supply
    // a custom style sheet or explicitly opt out of the default styling
    // by passing `undefined`. If a custom style sheet is desired, it can
    // be provided directly via the options.
    if ("styleSheet" in params) {
        return styleSheet ?? undefined;
    }
    if (isContainerModeEnabled(options ?? {})) {
        return createContainerDefaultStyleSheet(tagName);
    }
    return undefined;
}
/**
 * Creates a default style sheet for a container element.
 *
 * @remarks
 *
 * This style sheet sets the display property to "contents", which allows the
 * container to act as a transparent wrapper for its children, without adding
 * any additional layout or styling.
 */
function createContainerDefaultStyleSheet(tagName) {
    return css({
        container: {
            selector: tagName,
            styles: {
                display: "contents",
            },
        },
    });
}
